<script setup lang="ts">
import { useVModel } from '@vueuse/core'
import { computed } from 'vue'

import IconButton from '~/components/button/IconButton.vue'
import PaginationSelect from '~/components/select/PaginationSelect.vue'
import { IPagination } from '~/types/pagination.type'

interface IProps {
  modelValue: IPagination
  rowPerPageList?: number[]
  totalCount?: string | number
  hideRowsPerPage?: boolean
}

const props = defineProps<IProps>()

const value = useVModel(props, 'modelValue')
const maxPageNumber = computed(() => {
  if (!props.totalCount) return
  return Math.ceil(Number(props.totalCount) / value.value.rowPerPage)
})

const pageDown = () => {
  if (value.value.page === 1) return
  value.value.page = value.value.page - 1
}

const pageUp = () => {
  if (!maxPageNumber.value) return
  if (value.value.page >= maxPageNumber.value) return
  value.value.page = value.value.page + 1
}

const goFirstPage = () => {
  value.value.page = 1
}

const goLastPage = () => {
  if (!maxPageNumber.value) return
  value.value.page = maxPageNumber.value
}
</script>

<template>
  <div
    class="pagination-container"
    :class="{'hide-rows-per-page': hideRowsPerPage}"
  >
    <div
      v-if="!hideRowsPerPage"
      class="rows-per-page-cont"
    >
      <PaginationSelect
        v-model="value.rowPerPage"
        class="select"
        :options="rowPerPageList"
        @go-first-page="goFirstPage"
      />
      Rows Per Page
    </div>
    <div v-else />
    <div
      class="pagination-cont"
      v-if="maxPageNumber"
    >
      <IconButton
        icon="angle-double-left"
        @click="goFirstPage"
        size="lg"
      />
      <IconButton
        icon="angle-left"
        @click="pageDown"
        size="lg"
      />
      <span class="text">
        Page <span class="emphasis">{{ value.page.toLocaleString() }}</span> of <span class="emphasis">{{
          maxPageNumber.toLocaleString()
        }}</span>
      </span>
      <IconButton
        icon="angle-right"
        size="lg"
        @click="pageUp"
      />
      <IconButton
        icon="angle-double-right"
        size="lg"
        @click="goLastPage"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.pagination-container {
  @apply flex justify-between items-center text-primary/40 text-lg p-4;

  &.hide-rows-per-page {
    @apply p-0;
  }

  .select {
    margin-right: 8px;
  }

  .pagination-cont {
    @apply flex items-center;

    .text {
      display: inline-block;
      min-width: 150px;
      text-align: center;

      @include sm {
        min-width: 80px;
      }

      .emphasis {
        @apply text-primary font-bold;
      }
    }
  }

  @include sm {
    @apply flex-col gap-y-2;
    .rows-per-page-cont {
      @apply w-full;
    }
    .pagination-cont {
      @apply w-full;
      .text {
        @apply w-full;
      }
    }
  }
}
</style>